import React, { useEffect, useRef } from 'react'
import { useWebMidi } from '@/hooks/useWebMidi'
import { useMidiStore } from '@/stores/midiStore'
import {
  useMidiMappingStore,
  bindingMatches,
  type ControlDescriptor,
  type MidiBinding,
  type MidiBindingKind,
} from '@/stores/midiMappingStore'
import type { MidiMessage } from '@/lib/midiUtils'
import { MidiLearnOverlay } from '@/components/midi/MidiLearnOverlay'
import { MappedControlBadges } from '@/components/midi/MappedControlBadges'

/**
 * Root-level side effect provider: subscribes to the global MIDI
 * dispatch channel and drives mapping + learn behavior.
 *
 * Mount once from the app root (inside `PlaybackProvider`). Renders
 * nothing; all effects run as side effects of incoming messages.
 *
 * Dispatch rules:
 *   1. **Learn capture first.** When `learnMode` + `learnTargetId`
 *      are set AND the incoming message is a "meaningful" trigger
 *      (note-on vel>0, CC value>0, program change, pitchbend that
 *      escapes center), write a binding and clear `learnTargetId`.
 *      Learn mode itself stays on so the user can map several in a row.
 *   2. **Forwarding gate.** If `midiForwardingEnabled` is false,
 *      skip dispatch entirely (Learn still runs so the user can
 *      configure bindings before enabling forwarding).
 *   3. **Dispatch.** For every binding that matches (kind + channel
 *      + number + optional deviceId), resolve the `ControlDescriptor`,
 *      and invoke `trigger()` / `setValue()` with a kind-appropriate
 *      value.
 *
 * The provider does NOT gate on `document.activeElement` — external
 * MIDI doesn't cause keystrokes, so a focused text input doesn't
 * conflict. A mapped knob should still change a BPM value even if
 * the BPM input has focus.
 */
export const GlobalMidiProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { subscribeGlobalMidiIn, requestAccess } = useWebMidi()

  // Request MIDI access at app boot so hardware inputs are attached
  // even before the user opens the MIDI panel. The MidiRouter page
  // also calls `requestAccess` defensively; `sharedAccess` is a
  // singleton so multiple calls are no-ops.
  useEffect(() => {
    void requestAccess()
  }, [requestAccess])

  // Track last "value" per (inputId, binding-key) for rising-edge
  // detection. Keys are `${inputId}|${kind}|${channel}|${number}`.
  // The dispatch callback is stable (ref-based reads of store state),
  // so this Map survives for the provider's whole lifetime.
  const lastValueRef = useRef(new Map<string, number>())

  useEffect(() => {
    const unsub = subscribeGlobalMidiIn(({ message, inputId }) => {
      handleIncoming(message, inputId, lastValueRef.current)
    })
    return () => {
      unsub()
    }
  }, [subscribeGlobalMidiIn])

  return (
    <>
      <MidiLearnOverlay />
      <MappedControlBadges />
      {children}
    </>
  )
}

function handleIncoming(
  message: MidiMessage,
  inputId: string,
  lastValues: Map<string, number>,
): void {
  const mapKind = messageToBindingKind(message)
  if (!mapKind) return

  const mappingStore = useMidiMappingStore.getState()
  const { learnMode, learnTargetId, bindings, descriptors } = mappingStore

  // ── Learn capture ─────────────────────────────────────────────
  if (learnMode && learnTargetId) {
    const captured = captureBinding(message, mapKind, inputId)
    if (captured) {
      // Only add if an identical binding for this control doesn't
      // already exist — re-tapping the same key shouldn't produce
      // duplicate rows.
      const existing = mappingStore.bindings[learnTargetId] ?? []
      const dupe = existing.some(
        b =>
          b.kind === captured.kind &&
          b.channel === captured.channel &&
          b.number === captured.number &&
          b.deviceId === captured.deviceId,
      )
      if (!dupe) mappingStore.addBinding(learnTargetId, captured)
      mappingStore.setLearnTarget(null)
      return
    }
  }

  // ── Dispatch gate ─────────────────────────────────────────────
  const { midiForwardingEnabled } = useMidiStore.getState()
  if (!midiForwardingEnabled) return

  // Scan every bound control for matches. For the expected control
  // counts (tens, maybe a hundred), the nested iteration is fine.
  // If this ever gets hot, flip `bindings` into a pre-indexed map
  // keyed by `${kind}|${channel}|${number}`.
  for (const [controlId, controlBindings] of Object.entries(bindings)) {
    const descriptor = descriptors[controlId]
    if (!descriptor) continue
    for (const binding of controlBindings) {
      const channel = message.channel ?? 0
      const number = bindingNumberFromMessage(message) ?? 0
      if (!bindingMatches(binding, mapKind, channel, number, inputId)) continue
      dispatchToControl(binding, message, descriptor, inputId, lastValues)
    }
  }
}

function messageToBindingKind(message: MidiMessage): MidiBindingKind | null {
  switch (message.type) {
    case 'noteon':
    case 'noteoff':
      return 'note'
    case 'cc':
      return 'cc'
    case 'pitchbend':
      return 'pitchbend'
    case 'programchange':
      return 'programchange'
    case 'other':
      return null
  }
}

/** Extract the "number" a binding matches on from a message.
 *  For notes: `note`. CC: `cc`. Program: `value` (the program #).
 *  Pitchbend has no number axis. */
function bindingNumberFromMessage(message: MidiMessage): number | undefined {
  if (message.type === 'noteon' || message.type === 'noteoff')
    return message.note
  if (message.type === 'cc') return message.cc
  if (message.type === 'programchange') return message.value
  return undefined
}

/** Build a new binding from an incoming message + the control currently
 *  being learned. Returns null if the message isn't meaningful enough
 *  to capture (e.g. CC release-to-zero, tiny pitchbend jitter). */
function captureBinding(
  message: MidiMessage,
  kind: MidiBindingKind,
  inputId: string,
): Omit<MidiBinding, 'id'> | null {
  const channel = message.channel ?? 1
  const base = { channel, deviceId: inputId }

  switch (kind) {
    case 'note':
      // Only capture note-on rising edges so the release note-off
      // doesn't unbind / double-capture.
      if (message.type !== 'noteon' || (message.velocity ?? 0) <= 0) return null
      return { kind, number: message.note ?? 0, ...base }

    case 'cc':
      // Only capture CC values > 0. Many controllers send a 0 on
      // release for buttons; we want the "press" value, not the
      // "release" one, otherwise a button would capture ch#0.
      if (message.type !== 'cc' || (message.value ?? 0) <= 0) return null
      return { kind, number: message.cc ?? 0, ...base }

    case 'pitchbend': {
      // Require a sizable deflection from center (8192) to avoid
      // jitter-capture from an untouched wheel. 512 / 16383 ~ 3%.
      if (message.type !== 'pitchbend') return null
      const v = message.value ?? 8192
      if (Math.abs(v - 8192) < 512) return null
      return { kind, number: 0, ...base }
    }

    case 'programchange':
      if (message.type !== 'programchange') return null
      return { kind, number: message.value ?? 0, ...base }
  }
}

/** Invoke the control's handler with a value derived from the incoming
 *  message + binding shape. */
function dispatchToControl(
  binding: MidiBinding,
  message: MidiMessage,
  descriptor: ControlDescriptor,
  inputId: string,
  lastValues: Map<string, number>,
): void {
  const raw = rawValueFromMessage(message)
  if (raw === null) return

  const lastKey = `${inputId}|${binding.kind}|${binding.channel}|${binding.number}`
  const last = lastValues.get(lastKey) ?? 0
  lastValues.set(lastKey, raw)

  switch (descriptor.kind) {
    case 'trigger': {
      // Fire on rising edge only — prevents note-off / CC=0 release
      // from re-triggering.
      if (raw > 0 && last === 0) descriptor.trigger?.()
      return
    }

    case 'toggle': {
      // Flip on rising edge. Matches typical MIDI button behavior
      // (momentary push sends 127 / 0).
      if (raw > 0 && last === 0) {
        // Current value isn't readable from a descriptor-only API, so
        // we just call setValue(true) on rising edge and setValue(false)
        // on falling edge. Most toggle consumers interpret that as a
        // direct set; genuine "flip" behavior can be added later via
        // a getValue callback on the descriptor.
        descriptor.setValue?.(true)
      } else if (raw === 0 && last > 0) {
        descriptor.setValue?.(false)
      }
      return
    }

    case 'continuous': {
      const normalized = normalize(message, binding)
      const [lo, hi] = outputRange(descriptor, binding)
      const curve = binding.curve ?? 'linear'
      const shaped =
        curve === 'linear'
          ? normalized
          : curve === 'exp'
            ? normalized * normalized
            : Math.sqrt(Math.max(0, normalized))
      const v = lo + (hi - lo) * shaped
      descriptor.setValue?.(v)
      return
    }

    case 'discrete': {
      const values = descriptor.values
      if (!values || values.length === 0) return
      const normalized = normalize(message, binding)
      const idx = Math.min(
        values.length - 1,
        Math.max(0, Math.floor(normalized * values.length)),
      )
      descriptor.setValue?.(values[idx])
      return
    }
  }
}

/** Raw 0..1-ish numeric from a message, used for continuous mapping
 *  and rising-edge detection. Returns null for messages that don't
 *  carry a sensible numeric (other / note-off carries 0). */
function rawValueFromMessage(message: MidiMessage): number | null {
  switch (message.type) {
    case 'noteon':
      return message.velocity ?? 0
    case 'noteoff':
      return 0
    case 'cc':
      return message.value ?? 0
    case 'pitchbend':
      return message.value ?? 8192
    case 'programchange':
      return message.value ?? 0
    case 'other':
      return null
  }
}

/** Normalize an incoming message to 0..1 for continuous mapping. */
function normalize(message: MidiMessage, _binding: MidiBinding): number {
  switch (message.type) {
    case 'noteon':
      return (message.velocity ?? 0) / 127
    case 'noteoff':
      return 0
    case 'cc':
      return (message.value ?? 0) / 127
    case 'pitchbend':
      return (message.value ?? 8192) / 16383
    case 'programchange':
      return (message.value ?? 0) / 127
    default:
      return 0
  }
}

/** Resolve [min, max] for a continuous control: binding override
 *  first, then descriptor's declared range, then 0..1. */
function outputRange(
  descriptor: ControlDescriptor,
  binding: MidiBinding,
): [number, number] {
  const lo = binding.scaleMin ?? descriptor.min ?? 0
  const hi = binding.scaleMax ?? descriptor.max ?? 1
  return lo <= hi ? [lo, hi] : [hi, lo]
}
